import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { userRolesAPI } from '../services/api'

export const useUserRolesStore = defineStore('userRoles', () => {
  // State
  const roles = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const activeRoles = computed(() => 
    roles.value.filter(role => role.is_active)
  )

  const rolesMap = computed(() => {
    const map = {}
    activeRoles.value.forEach(role => {
      map[role.name] = role
    })
    return map
  })

  const getRoleDisplayName = (roleName) => {
    const role = rolesMap.value[roleName]
    return role ? role.display_name : roleName
  }

  const getRoleColorClass = (roleName) => {
    const role = rolesMap.value[roleName]
    return role ? role.color_class : 'bg-gray-100 text-gray-800'
  }

  const getRolePermissions = (roleName) => {
    const role = rolesMap.value[roleName]
    return role ? role.permissions : []
  }

  // Actions
  const fetchRoles = async () => {
    try {
      loading.value = true
      error.value = null
      
      const response = await userRolesAPI.getRoles()
      roles.value = response.roles || []
    } catch (err) {
      console.error('Failed to fetch user roles:', err)
      error.value = err.response?.data?.message || 'Failed to fetch user roles'
      
      // Fallback to default roles if API fails
      roles.value = [
        {
          id: 1,
          name: 'customer',
          display_name: 'Customer',
          color_class: 'bg-blue-100 text-blue-800',
          permissions: ['view_catalogue', 'place_orders', 'view_own_orders'],
          is_active: true,
          sort_order: 1
        },
        {
          id: 2,
          name: 'workshop',
          display_name: 'Workshop',
          color_class: 'bg-purple-100 text-purple-800',
          permissions: ['view_workshop_jobs', 'update_job_status', 'view_orders'],
          is_active: true,
          sort_order: 2
        },
        {
          id: 3,
          name: 'admin',
          display_name: 'Admin',
          color_class: 'bg-red-100 text-red-800',
          permissions: ['manage_users', 'manage_products', 'manage_orders', 'manage_labels', 'view_analytics'],
          is_active: true,
          sort_order: 3
        }
      ]
    } finally {
      loading.value = false
    }
  }

  const createRole = async (roleData) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await userRolesAPI.createRole(roleData)
      roles.value.push(response.role)
      return response.role
    } catch (err) {
      console.error('Failed to create user role:', err)
      error.value = err.response?.data?.message || 'Failed to create user role'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateRole = async (roleId, roleData) => {
    try {
      loading.value = true
      error.value = null
      
      const response = await userRolesAPI.updateRole(roleId, roleData)
      const index = roles.value.findIndex(role => role.id === roleId)
      if (index !== -1) {
        roles.value[index] = response.role
      }
      return response.role
    } catch (err) {
      console.error('Failed to update user role:', err)
      error.value = err.response?.data?.message || 'Failed to update user role'
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteRole = async (roleId) => {
    try {
      loading.value = true
      error.value = null
      
      await userRolesAPI.deleteRole(roleId)
      roles.value = roles.value.filter(role => role.id !== roleId)
    } catch (err) {
      console.error('Failed to delete user role:', err)
      error.value = err.response?.data?.message || 'Failed to delete user role'
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  return {
    // State
    roles,
    loading,
    error,
    
    // Getters
    activeRoles,
    rolesMap,
    getRoleDisplayName,
    getRoleColorClass,
    getRolePermissions,
    
    // Actions
    fetchRoles,
    createRole,
    updateRole,
    deleteRole,
    clearError
  }
})
